import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { BehaviorSubject, NEVER, Observable, Subject, Subscription, combineLatest, debounceTime, map, merge, of, startWith, switchMap } from 'rxjs';
import { CreatedTrailer, Trailer, TrailerStatus } from '../../models/trailer';
import { SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { Carrier } from '@shared/models/carrier';
import { TrailerService } from '../../services/trailer.service';
import { Dropdown } from 'primeng/dropdown';
import { ShiftContextService } from '../../services/shift-context.service';
import { DateTime } from 'luxon';

export type eligibleForValues = 'arriving' | 'unloading' | 'loading' | 'departing' | 'any';

@Component({
    selector: 'app-trailer-select',
    templateUrl: './trailer-select.component.html',
    styleUrls: ['./trailer-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => TrailerSelectComponent),
        }
    ]
})
export class TrailerSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @ViewChild('dropdown') dropdown: Dropdown;
    @ViewChild('addButton') addButton: ElementRef<HTMLButtonElement>;

    _siteId = new BehaviorSubject<string | null>(null);
    _date = new BehaviorSubject<DateTime | null>(null);
    _siteSubinventoryId = new BehaviorSubject<number | null>(null);
    _carrier = new BehaviorSubject<Carrier | null>(null);
    _eligibleFor = new BehaviorSubject<eligibleForValues>('arriving');

    @Input() set eligibleFor(val: eligibleForValues) {
        this._eligibleFor.next(val);
    }

    @Input() set siteSubinventoryId(val: number | null) {
        this._siteSubinventoryId.next(val);
    }

    @Input() canChooseSubinventory: boolean = false;

    @Input() set carrier(val: Carrier) {
        this._carrier.next(val);
    }

    @Input() canChooseCarrier: boolean = false;

    @Input() set value(val: number) {
        this.trailerId = val;

        setTimeout(() => this.trailersRefresh.next());
    }

    /**
     * Hides passed trailers from the dropdown.
     */
    @Input() excludeTrailerIds: number[] = [];
    @Input() inputId: string | null = null;
    @Input() readonly: boolean = false;
    @Input() name: string | null = null;
    @Input() tabindex: number | null = null;
    @Input() addButtonTabIndex: number = -1;
    @Input() disabled: boolean;

    @Output() input = new EventEmitter<number>();

    @Output() trailer = new EventEmitter<Trailer>();

    trailerId: number | null = null;

    showAddDialog: boolean = false;

    trailersRefresh = new Subject<void>();

    trailers: Trailer[] = [];

    subinventorySelects = new BehaviorSubject<SelectItem[]>([]);
    subinventoryName = combineLatest([this._siteSubinventoryId, this.subinventorySelects])
        .pipe(map(([id, selects]) => selects.find(s => s.value === id)?.label ?? ''));

    addForm = new FormGroup({
        trailerNumber: new FormControl<string>('', [ Validators.required, Validators.maxLength(50) ]),
        carrierId: new FormControl<number>(null, Validators.required),
        subinventoryId: new FormControl<number>(null, Validators.required)
    });

    private subscriptions: Subscription[] = [];
    private changeListeners: ((val: number) => void)[] = [];
    private touchListeners: (() => void)[] = [];

    constructor(
        private contextService: ShiftContextService,
        private trailersService: TrailerService,
        private messageService: MessageService) {
    }

    ngOnInit(): void {
        this.showAddDialog = false;

        this.subscriptions = [
            this.contextService.CurrentContext.subscribe(context => {
                this._siteId.next(context.site.id);
                this._date.next(context.date);
                this.subinventorySelects.next(context.site.subinventories
                    .filter(s => s.isActive)
                    .map(s => ({value: s.id, label: s.name})));
            }),

            this._siteSubinventoryId.subscribe(subinventoryId => {
                this.addForm.controls.subinventoryId.setValue(subinventoryId);
            }),

            this._carrier.subscribe(carrier => {
                this.addForm.controls.carrierId.setValue(carrier?.id ?? null);
            }),

            merge(
                this.trailersRefresh.pipe(map(() => <[string, DateTime, number, Carrier, eligibleForValues]>[
                    this._siteId.value,
                    this._date.value,
                    this._siteSubinventoryId.value,
                    this._carrier.value,
                    this._eligibleFor.value
                ])),
                combineLatest([
                    this._siteId,
                    this._date,
                    this._siteSubinventoryId,
                    this._carrier,
                    this._eligibleFor
                ]))
                .pipe(
                    switchMap(([siteId, date, subinventoryId, carrier, eligibleFor]) => {
                        const statuses = eligibleFor === 'arriving'
                            ? [ TrailerStatus.Planned ]
                            : eligibleFor === 'unloading'
                            ? [TrailerStatus.Planned, TrailerStatus.ArrivedInbound]
                            : eligibleFor === 'loading'
                            ? [TrailerStatus.Empty, TrailerStatus.Planned]
                            : eligibleFor === 'departing'
                            ? [TrailerStatus.Empty, TrailerStatus.LoadedOutbound, TrailerStatus.Planned]
                            : [];

                        if (siteId && date)
                            return this.trailersService.GetFreeTrailers(siteId, subinventoryId, date, carrier?.id ?? null, statuses, this.trailerId);
                        else
                            return of(null);
                    }))
                .subscribe({
                    next: trailers => {
                        if (trailers)
                            setTimeout(() => {
                                this.trailers = trailers.filter(t => !this.excludeTrailerIds.includes(t.id));
                            });
                    },
                    error: _err => {}
                }),
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
        this.changeListeners = [];
        this.touchListeners = [];

        this._siteId.next(null);
        this._date.next(null);
        this._siteSubinventoryId.next(null);
        this._carrier.next(null);
        this._eligibleFor.next('arriving');
        this.subinventorySelects.next([]);
    }

    GetStatusString(status: TrailerStatus): string {
        return status === TrailerStatus.Planned
            ? 'Planned'
            : status === TrailerStatus.ArrivedInbound
            ? 'Arrived Inbound'
            : status === TrailerStatus.Empty
            ? 'Empty'
            : status === TrailerStatus.LoadedOutbound
            ? 'Loaded Outbound'
            : status === TrailerStatus.Left
            ? 'Left'
            : 'Unknown';
    }

    showing: boolean = false;

    OnFocus(event: FocusEvent) {
        if (!this.showing)
            setTimeout(() => this.dropdown.show());
        this.touchListeners.forEach(f => f());
    }

    OnBlur() {
    }

    OnShow() {
        this.showing = true;
    }

    OnHide() {
        this.showing = false;
        this.addButton.nativeElement.focus();
    }

    OnChange() {
        this.trailersRefresh.next();
        this.changeListeners.forEach(f => f(this.trailerId));
    }

    SaveNewTrailer() {
        if (!this.addForm.valid) {
            this.addForm.markAsTouched();

            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Create Trailer',
                detail: 'Please correct the listed errors.'
            });
            return;
        }

        const post: CreatedTrailer = {
            siteId: this._siteId.value,
            siteSubinventoryId: this.addForm.controls.subinventoryId.value,
            carrierId: this.addForm.controls.carrierId.value,
            trailerNumber: this.addForm.controls.trailerNumber.value,
            arrivedAt: null
        }

        this.trailersService.CreateTrailer(post).subscribe({
            next: trailer => {
                this.trailerId = trailer.id;
                this.addForm.reset({
                    trailerNumber: '',
                    carrierId: this._carrier.value?.id ?? null,
                    subinventoryId: this._siteSubinventoryId.value ?? null
                });
                this.trailersRefresh.next();
                this.showAddDialog = false;
                setTimeout(() => this.OnChange());
            },
            error: _err => {}
        });
    }

    registerOnTouched(fn: () => void): void {
        this.touchListeners.push(fn);
    }

    registerOnChange(fn: (val: number) => void): void {
        this.changeListeners.push(fn);
    }

    writeValue(obj: number): void {
        this.value = obj;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
